import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ContentTag } from '@/components/ui/content-tag';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Globe, Lock, User, CheckCircle, MessageCircle, Download, Clipboard, X, Sparkles } from 'lucide-react';
import JSZip from 'jszip';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useWebsites } from '@/context/WebsitesContext';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { useSettingsState } from '@/context/SettingsStateContext';
import { useWordPress } from '@/context/WordPressContext';
import useWordpressCategories from '@/hooks/useWordpressCategories';
import useWordpressSettings from '@/hooks/useWordpressSettings';
import { parseBulkKeywords } from '@/utils/keywordUtils';

interface SecondStageOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  initialStep?: 1 | 2 | 3 | 4;
}

export default function SecondStageOnboardingModal({ isOpen, onClose, onComplete, initialStep }: SecondStageOnboardingModalProps) {
  const { currentWebsite } = useWebsites();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [wpUrl, setWpUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [wpUsername, setWpUsername] = useState('');
  const [wpPassword, setWpPassword] = useState('');
  const [usePlugin, setUsePlugin] = useState(true); // Default to plugin approach (Simple)
  const [pluginDownloaded, setPluginDownloaded] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const { updateWordPressPublishStatus } = useWordpressSettings();
  const { weeklyPlanningDay, setWeeklyPlanningDay, postingFrequency, writingStyle, subjectMatters, formattemplate, imagePrompt, imageModel, negativePrompt, contentPolicies, updateSettingsInDatabase } = useSettings();
  const { wpPublishStatus } = useSettingsState();
  const [publishToWordPress, setPublishToWordPress] = useState(wpPublishStatus === 'publish');
  const [localWeeklyPlanningDay, setLocalWeeklyPlanningDay] = useState(weeklyPlanningDay || 'friday');
  const { settings: wpSettings, refreshSettings } = useWordPress();
  const { fetchCategoriesFromWordPress, generateCategoryIdeas, addCategory } = useWordpressCategories();
  const settingsState = useSettingsState();
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [keywordSuggestions, setKeywordSuggestions] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');

  const getWebsiteUrl = () => {
    if (currentWebsite?.url) return currentWebsite.url;
    if (wpUrl) return wpUrl;
    return '';
  };

  const totalSteps = wpSettings?.is_connected ? 4 : 3;

  // Handle keyword addition
  const handleAddKeywords = () => {
    const newKeywords = parseBulkKeywords(keywordInput, settingsState.subjects);
    if (newKeywords.length > 0) {
      settingsState.setSubjects([...settingsState.subjects, ...newKeywords]);
      setKeywordInput('');
    }
  };

  // Ensure the correct starting step when the modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('Modal opened with currentWebsite:', currentWebsite);
      console.log('Modal opened with initialStep:', initialStep);
      setStep(initialStep || 1);
    }
  }, [isOpen, initialStep, currentWebsite]);

  const parseWordPressData = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      
      // Parse the copied text format:
      // WordPress URL: https://openocean.dk
      // Username: philiplethcom
      // Application Password: ZlidUdf3TwFJvdUOmJtBXnKh
      
      const lines = clipboardText.split('\n');
      let wpUrl = '';
      let username = '';
      let password = '';
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith('WordPress URL:')) {
          wpUrl = trimmedLine.replace('WordPress URL:', '').trim();
        } else if (trimmedLine.startsWith('Username:')) {
          username = trimmedLine.replace('Username:', '').trim();
        } else if (trimmedLine.startsWith('Application Password:')) {
          password = trimmedLine.replace('Application Password:', '').trim();
        }
      }
      
      if (username && password) {
        setWpUsername(username);
        setWpPassword(password);
        
        // If we have a URL, update the URL field too
        if (wpUrl) {
          setWpUrl(wpUrl);
        }
        
        toast.success('WordPress data inserted successfully!');
      } else {
        toast.error('Could not parse WordPress data. Please make sure you copied the data from the WordPress plugin.');
      }
    } catch (error) {
      console.error('Error reading clipboard:', error);
      toast.error('Could not read clipboard. Please paste the data manually.');
    }
  };

  // Pre-fill WordPress URL with current website URL
  useEffect(() => {
    if (currentWebsite?.url && !wpUrl) {
      setWpUrl(currentWebsite.url);
    }
  }, [currentWebsite?.url, wpUrl]);

  const handleWordPressConnection = async () => {
    if (!currentWebsite?.id) {
      toast.error('No website selected');
      return;
    }

    if (!wpUrl) {
      setError('Please enter your WordPress URL');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Clean the URL
      let cleanUrl = wpUrl.trim();
      if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
        cleanUrl = 'https://' + cleanUrl;
      }
      cleanUrl = cleanUrl.replace(/\/+$/, '');

      // Show connection method options instead of jumping to step 2
      setShowInstructions(true);
      setIsLoading(false);

    } catch (error) {
      console.error('WordPress connection error:', error);
      setError(error instanceof Error ? error.message : 'Failed to setup WordPress connection');
      toast.error('Failed to setup WordPress connection');
      setIsLoading(false);
    }
  };

  const handleDownloadPlugin = async () => {
    await downloadPluginZip();
  };

  const handleManualConnection = async () => {
    await handleWordPressCredentials();
  };

  const handleOtherCms = async () => {
    if (!currentWebsite?.id) {
      toast.error('No website selected');
      return;
    }

    setIsLoading(true);

    try {
      const { data, error: responseError } = await supabase.functions.invoke('second-stage-onboarding', {
        body: {
          action: 'other_cms',
          website_id: currentWebsite.id
        }
      });

      if (responseError) {
        throw responseError;
      }

      if (data?.success) {
        toast.success('Thank you! We\'ll reach out to discuss integration options.');
        onComplete();
      } else {
        throw new Error(data?.message || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Other CMS request error:', error);
      toast.error('Failed to submit request');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    if (!currentWebsite?.id) {
      toast.error('No website selected');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('websites')
        .update({
          second_stage_onboarding_completed: true,
          second_stage_skipped: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentWebsite.id);

      if (error) {
        throw error;
      }

      toast.success('You can always connect WordPress later in Settings');
      // Move to preferences step instead of closing
      setStep(2);
    } catch (error) {
      console.error('Skip onboarding error:', error);
      toast.error('Failed to skip onboarding');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDontShowAgain = async () => {
    if (!currentWebsite?.id) {
      toast.error('No website selected');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('websites')
        .update({
          second_stage_onboarding_completed: true,
          second_stage_skipped: true,
          second_stage_dont_show_again: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentWebsite.id);

      if (error) {
        throw error;
      }

      toast.success('You can always connect WordPress later in Settings');
      onComplete();
    } catch (error) {
      console.error('Don\'t show again error:', error);
      toast.error('Failed to set preference');
    } finally {
      setIsLoading(false);
    }
  };

  const openWordPressAdmin = () => {
    if (!wpUrl) return;
    
    let cleanUrl = wpUrl.trim();
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }
    cleanUrl = cleanUrl.replace(/\/+$/, '');

    const wpAdminUrl = `${cleanUrl}/wp-admin/profile.php#application-passwords-section`;
    window.open(wpAdminUrl, '_blank');
  };

  const openWordPressPluginsPage = () => {
    if (!wpUrl) return;
    
    let cleanUrl = wpUrl.trim();
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }
    cleanUrl = cleanUrl.replace(/\/+$/, '');

    const wpAddNewPluginUrl = `${cleanUrl}/wp-admin/plugin-install.php?tab=upload`;
    window.open(wpAddNewPluginUrl, '_blank');
  };

  const downloadPluginZip = async () => {
    try {
      const zip = new JSZip();
      
      // Create the plugin directory structure
      const pluginFolder = zip.folder('contentgardener-connector');
      
      // Add the main plugin file
      const pluginContent = `<?php
/**
 * Plugin Name: ContentGardener Connector
 * Plugin URI: https://contentgardener.ai
 * Description: Simple connector for ContentGardener.ai - automatically creates secure connection keys
 * Version: 1.0.0
 * Author: ContentGardener
 * License: GPL v2 or later
 * Text Domain: contentgardener-connector
 */

// Prevent direct access
if (!defined('ABSPATH')) {
    exit;
}

class ContentGardenerConnector {
    
    private $app_password_name = 'ContentGardener';
    private $option_name = 'contentgardener_connection_data';
    
    public function __construct() {
        add_action('admin_menu', array($this, 'add_admin_menu'));
        add_action('admin_init', array($this, 'handle_activation'));
    }
    
    public function handle_activation() {
        if (get_option('contentgardener_plugin_activated') !== '1.0.0') {
            $this->auto_generate_password();
            update_option('contentgardener_plugin_activated', '1.0.0');
        }
    }
    
    private function auto_generate_password() {
        $current_user = wp_get_current_user();
        
        if (!$current_user->exists()) {
            return;
        }
        
        $existing_passwords = WP_Application_Passwords::get_user_application_passwords($current_user->ID);
        foreach ($existing_passwords as $password) {
            if ($password['name'] === $this->app_password_name) {
                return;
            }
        }
        
        $password_data = WP_Application_Passwords::create_new_application_password(
            $current_user->ID,
            array(
                'name' => $this->app_password_name,
                'capabilities' => array('read', 'edit_posts', 'publish_posts')
            )
        );
        
        if (!is_wp_error($password_data)) {
            $connection_data = array(
                'username' => $current_user->user_login,
                'password' => $password_data[0],
                'created_at' => current_time('mysql'),
                'site_url' => get_site_url()
            );
            
            update_option($this->option_name, $connection_data);
        }
    }
    
    public function add_admin_menu() {
        add_menu_page(
            'ContentGardener',
            'ContentGardener',
            'manage_options',
            'contentgardener-connector',
            array($this, 'admin_page'),
            'dashicons-admin-generic',
            30
        );
    }
    
    public function admin_page() {
        $connection_data = get_option($this->option_name);
        ?>
        <div class="wrap">
            <h1>ContentGardener Connector</h1>
            
            <?php if ($connection_data): ?>
                <div class="notice notice-success">
                    <p><strong>✅ Connection Ready!</strong> Your WordPress site is ready to connect with ContentGardener.ai</p>
                </div>
                
                <div class="card" style="max-width: 600px;">
                    <h2>Connection Details</h2>
                    <p>Use these details to connect your WordPress site to ContentGardener.ai:</p>
                    
                    <table class="form-table">
                        <tr>
                            <th scope="row">WordPress URL:</th>
                            <td><code><?php echo esc_html($connection_data['site_url']); ?></code></td>
                        </tr>
                        <tr>
                            <th scope="row">Username:</th>
                            <td><code><?php echo esc_html($connection_data['username']); ?></code></td>
                        </tr>
                        <tr>
                            <th scope="row">Application Password:</th>
                            <td>
                                <input type="text" 
                                       id="app-password" 
                                       value="<?php echo esc_attr($connection_data['password']); ?>" 
                                       readonly 
                                       style="width: 100%; font-family: monospace; background: #f0f0f0;"
                                       onclick="this.select();">
                                <p class="description">Click to select and copy this password</p>
                            </td>
                        </tr>
                    </table>
                    
                    <div style="margin-top: 20px;">
                        <button type="button" class="button button-primary" onclick="copyPassword()">
                            📋 Copy All Details
                        </button>
                    </div>
                    
                    <div style="margin-top: 20px; padding: 15px; background: #f9f9f9; border-left: 4px solid #0073aa;">
                        <h3>Next Steps:</h3>
                        <ol>
                            <li>Copy the Application Password above</li>
                            <li>Go to your ContentGardener.ai dashboard</li>
                            <li>Paste the password in the connection form</li>
                            <li>Click "Connect WordPress"</li>
                        </ol>
                    </div>
                </div>
                
                <script>
                function copyPassword() {
                    const password = document.getElementById('app-password').value;
                    const username = '<?php echo esc_js($connection_data['username']); ?>';
                    const siteUrl = '<?php echo esc_js($connection_data['site_url']); ?>';
                    
                    const details = \`WordPress URL: \${siteUrl}\\nUsername: \${username}\\nApplication Password: \${password}\`;
                    
                    navigator.clipboard.writeText(details).then(function() {
                        alert('Connection details copied to clipboard!');
                    });
                }
                </script>
                
            <?php else: ?>
                <div class="notice notice-error">
                    <p><strong>❌ Connection Not Ready</strong> There was an error creating the connection. Please try again.</p>
                </div>
                
                <div class="card" style="max-width: 600px;">
                    <h2>Manual Setup</h2>
                    <p>If automatic setup failed, you can manually create an Application Password:</p>
                    
                    <ol>
                        <li>Go to <a href="<?php echo admin_url('profile.php#application-passwords-section'); ?>">Your Profile</a></li>
                        <li>Scroll down to "Application Passwords"</li>
                        <li>Enter "ContentGardener" as the name</li>
                        <li>Click "Add New Application Password"</li>
                        <li>Copy the generated password</li>
                        <li>Use it in ContentGardener.ai</li>
                    </ol>
                    
                    <button type="button" class="button button-primary" onclick="location.reload()">
                        🔄 Try Auto-Setup Again
                    </button>
                </div>
            <?php endif; ?>
        </div>
        <?php
    }
}

new ContentGardenerConnector();
register_activation_hook(__FILE__, function() {});
`;
      
      // Add the main plugin file
      pluginFolder?.file('contentgardener-connector.php', pluginContent);
      
      // Add a README file
      const readmeContent = `# ContentGardener Connector

A simple WordPress plugin that automatically creates secure connection keys for ContentGardener.ai.

## Installation

1. Upload the plugin files to the /wp-content/plugins/contentgardener-connector/ directory
2. Activate the plugin through the 'Plugins' menu in WordPress
3. Go to 'ContentGardener' in your WordPress admin menu
4. Copy the connection details and use them in ContentGardener.ai

## Features

- Automatically generates Application Passwords
- Provides a simple interface to get connection details
- Secure and follows WordPress best practices

## Support

For support, visit https://contentgardener.ai
`;
      
      pluginFolder?.file('README.md', readmeContent);
      
      // Generate the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Download the zip file
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contentgardener-connector.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setPluginDownloaded(true);
      toast.success('Plugin downloaded successfully!');
    } catch (error) {
      console.error('Error creating plugin zip:', error);
      toast.error('Failed to download plugin');
    }
  };

  const handleWordPressCredentials = async () => {
    console.log('handleWordPressCredentials called with currentWebsite:', currentWebsite);
    
    if (!currentWebsite?.id) {
      console.error('No website selected - currentWebsite:', currentWebsite);
      toast.error('No website selected');
      return;
    }

    if (!wpUsername || !wpPassword) {
      setError('Please enter both username and application password');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Clean the URL
      let cleanUrl = wpUrl.trim();
      if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
        cleanUrl = 'https://' + cleanUrl;
      }
      cleanUrl = cleanUrl.replace(/\/+$/, '');

      // Validate all required fields
      console.log('Validating fields:', {
        cleanUrl: !!cleanUrl,
        wpUsername: !!wpUsername,
        wpPassword: !!wpPassword,
        websiteId: !!currentWebsite?.id
      });
      
      if (!cleanUrl) {
        throw new Error('WordPress URL is required');
      }
      if (!wpUsername) {
        throw new Error('WordPress username is required');
      }
      if (!wpPassword) {
        throw new Error('WordPress application password is required');
      }
      if (!currentWebsite?.id) {
        throw new Error('Website ID is required');
      }

      // Debug logging
      console.log('Sending WordPress connect request:', {
        action: 'connect_wordpress',
        website_id: currentWebsite.id,
        url: cleanUrl,
        username: wpUsername,
        password: wpPassword ? '[REDACTED]' : 'undefined'
      });

      const { data, error: responseError } = await supabase.functions.invoke('wordpress-connect', {
        body: {
          action: 'connect_wordpress',
          website_id: currentWebsite.id,
          url: cleanUrl,
          username: wpUsername,
          password: wpPassword
        }
      });

      if (responseError) {
        throw responseError;
      }

      if (data?.success) {
        toast.success(`WordPress connected successfully! Welcome, ${data.wordpress_user || 'User'}!`);
        
        // Update the WordPress context state to reflect the new connection
        if (data.settings) {
          // Refresh WordPress settings in the context to update the dashboard immediately
          try {
            await refreshSettings();
          } catch (error) {
            console.error('Failed to refresh WordPress settings:', error);
          }
        }
        
        // Mark second stage onboarding as completed
        try {
          await supabase
            .from('websites')
            .update({
              second_stage_onboarding_completed: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', currentWebsite.id);
          console.log('Successfully marked second stage onboarding as completed');
        } catch (error) {
          console.error('Failed to mark onboarding as completed:', error);
        }
        
        setShowInstructions(false);
        onComplete();
      } else {
        throw new Error(data?.message || 'Failed to connect WordPress');
      }
    } catch (error) {
      console.error('WordPress credentials error:', error);
      setError(error instanceof Error ? error.message : 'Failed to connect WordPress');
      toast.error('Failed to connect WordPress');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl w-[720px] md:w-[900px] max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              {step === 1 && 'Connect your website'}
              {step === 2 && 'Publishing preferences'}
              {step === 3 && 'Create keywords'}
              {step === 4 && 'Categories'}
            </DialogTitle>
            <div className="text-sm text-muted-foreground">Step {step} of {totalSteps}</div>
          </div>
          <DialogDescription>
            {step === 1 && "Let's connect your website to start publishing content automatically. We currently support WordPress, but we're working on other CMS integrations."}
            {step === 2 && 'Choose how content is handled and when we plan your week.'}
            {step === 3 && 'Add your core keywords to guide content planning.'}
            {step === 4 && 'Fetch your current WordPress categories and add recommendations.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-6">
          {step === 1 && (
          <div className="space-y-6">
          {/* WordPress Connection Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Connect your WordPress site
              </CardTitle>
              <CardDescription>
                Connect your WordPress site to automatically publish content
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wpUrl">WordPress URL</Label>
                <Input
                  id="wpUrl"
                  type="url"
                  placeholder="https://yourwebsite.com"
                  value={wpUrl}
                  onChange={(e) => setWpUrl(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button 
                onClick={handleWordPressConnection} 
                disabled={isLoading || !wpUrl}
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Choose Connection Method
                  </>
                )}
              </Button>
              
              <div className="text-center">
                <p className="text-xs text-muted-foreground">
                  Choose between our simple plugin setup or manual Application Password creation.
                </p>
              </div>

              {showInstructions && (
                <div className="border rounded-lg p-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Connect WordPress</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowInstructions(false)}
                    >
                      ✕
                    </Button>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Choose your preferred method to connect your WordPress site
                  </p>

                  <div className="flex items-center justify-center p-4 bg-muted rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className={`text-sm font-medium transition-colors ${!usePlugin ? 'text-primary' : 'text-muted-foreground'}`}>
                        Simple (Plugin)
                      </span>
                      <button
                        onClick={() => setUsePlugin(!usePlugin)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                          !usePlugin ? 'bg-primary' : 'bg-muted-foreground/20'
                        }`}
                        aria-label={`Switch to ${!usePlugin ? 'Advanced' : 'Simple'} mode`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            !usePlugin ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className={`text-sm font-medium transition-colors ${usePlugin ? 'text-primary' : 'text-muted-foreground'}`}>
                        Advanced (Manual)
                      </span>
                    </div>
                  </div>

                  {usePlugin && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-medium text-blue-900 mb-2">Simple Plugin Method</h4>
                        <p className="text-sm text-blue-700 mb-4">
                          We'll create a custom plugin for your WordPress site that handles the connection automatically.
                        </p>
                        <Button
                          onClick={handleDownloadPlugin}
                          disabled={isLoading}
                          className="w-full"
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Creating Plugin...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-2" />
                              Download Plugin
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {!usePlugin && (
                    <div className="space-y-4">
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <h4 className="font-medium text-orange-900 mb-2">Advanced Manual Method</h4>
                        <p className="text-sm text-orange-700 mb-4">
                          Create an Application Password in WordPress and enter the credentials below.
                        </p>
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="wpUsername">WordPress Username</Label>
                            <Input
                              id="wpUsername"
                              type="text"
                              value={wpUsername}
                              onChange={(e) => {
                                console.log('Username input changed:', e.target.value);
                                setWpUsername(e.target.value);
                              }}
                              placeholder="Enter your WordPress username"
                            />
                          </div>
                          <div>
                            <Label htmlFor="wpPassword">Application Password</Label>
                            <Input
                              id="wpPassword"
                              type="password"
                              value={wpPassword}
                              onChange={(e) => {
                                console.log('Password input changed:', e.target.value ? '[REDACTED]' : 'empty');
                                setWpPassword(e.target.value);
                              }}
                              placeholder="Enter your Application Password"
                            />
                          </div>
                          <Button
                            onClick={handleManualConnection}
                            disabled={isLoading || !wpUsername || !wpPassword}
                            className="w-full"
                          >
                            {isLoading ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Connecting...
                              </>
                            ) : (
                              <>
                                <Lock className="h-4 w-4 mr-2" />
                                Connect WordPress
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Other CMS Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                I use another CMS
              </CardTitle>
              <CardDescription>
                Let us know what CMS you're using and we'll reach out to discuss integration options
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                variant="outline" 
                onClick={handleOtherCms} 
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Request other CMS integration'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Actions (aligned like step 2) */}
          <div className="flex items-center justify-between">
            <div />
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSkip} disabled={isLoading}>Skip</Button>
              <Button onClick={() => setStep(2)} disabled={isLoading}>Continue</Button>
            </div>
          </div>
          </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Publishing preference</CardTitle>
                  <CardDescription>Publish to WordPress or keep as drafts only.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Publish in WordPress</Label>
                      <p className="text-sm text-muted-foreground">Turn off to keep everything as drafts.</p>
                    </div>
                    <Switch checked={publishToWordPress} onCheckedChange={setPublishToWordPress} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Weekly planning day</CardTitle>
                  <CardDescription>Select the main day for weekly planning.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label>Planning day</Label>
                    <Select value={localWeeklyPlanningDay} onValueChange={setLocalWeeklyPlanningDay}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select planning day" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monday">Monday</SelectItem>
                        <SelectItem value="tuesday">Tuesday</SelectItem>
                        <SelectItem value="wednesday">Wednesday</SelectItem>
                        <SelectItem value="thursday">Thursday</SelectItem>
                        <SelectItem value="friday">Friday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={() => setStep(1)} disabled={isLoading}>Back</Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { toast.info('Skipped. You can update these later in Settings.'); setStep(3); }} disabled={isLoading}>Skip</Button>
                  <Button onClick={async () => {
                  try {
                    if (currentWebsite?.id) {
                      await updateWordPressPublishStatus(publishToWordPress ? 'publish' : 'draft', { silent: true });
                      setWeeklyPlanningDay(localWeeklyPlanningDay);
                      await updateSettingsInDatabase(
                        postingFrequency,
                        writingStyle,
                        subjectMatters,
                        formattemplate,
                        imagePrompt,
                        imageModel,
                        negativePrompt,
                        localWeeklyPlanningDay,
                        contentPolicies
                      );
                    }
                  } catch (e) {
                    console.error('Saving preferences failed', e);
                  }
                  // Proceed to next step
                  try {
                    const { data } = await supabase
                      .from('wordpress_settings')
                      .select('is_connected')
                      .eq('website_id', currentWebsite?.id)
                      .maybeSingle();
                    const isConnected = Boolean(data?.is_connected);
                    if (isConnected) {
                      toast.success('Preferences saved. Continue to keywords.');
                    } else {
                      toast.success('Preferences saved. Continue to keywords.');
                    }
                  } catch {}
                  setStep(3);
                }} disabled={isLoading}>Continue</Button>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Create keywords</CardTitle>
                  <CardDescription>Add the primary keywords you want us to focus on.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <Label>Keywords</Label>
                    <div className="flex flex-wrap gap-2">
                      {settingsState.subjects.map((kw, index) => (
                        <Badge key={index} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {kw}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 rounded-full p-0 ml-1 text-blue-700 hover:bg-blue-200 hover:text-blue-800"
                            onClick={() => {
                              try {
                                const updated = settingsState.subjects.filter((s) => s !== kw);
                                settingsState.setSubjects(updated);
                              } catch (e) {
                                console.error('Failed removing keyword', e);
                              }
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                    <div className="space-y-2 mt-2">
                      <Textarea
                        placeholder="Add keywords (single or multiple). You can paste from Excel - keywords will be separated by tabs, newlines, commas, or semicolons."
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        className="min-h-[100px]"
                      />
                      <div className="flex gap-2">
                        <Button onClick={handleAddKeywords} disabled={!keywordInput.trim()}>
                          <Sparkles className="h-4 w-4 mr-1" />
                          Add {parseBulkKeywords(keywordInput, settingsState.subjects).length} Keywords
                        </Button>
                        <Button variant="outline" onClick={() => setKeywordInput("")}>
                          Clear
                        </Button>
                      </div>
                      {keywordInput.trim() && (
                        <p className="text-sm text-muted-foreground">
                          {parseBulkKeywords(keywordInput, settingsState.subjects).length} keywords will be added (duplicates will be skipped)
                        </p>
                      )}
                    </div>
                    {keywordSuggestions.length > 0 && (
                      <div className="space-y-2 mt-4">
                        <Label>Suggestions</Label>
                        <div className="flex flex-wrap gap-2">
                          {keywordSuggestions.map((kw) => (
                            <Button
                              key={kw}
                              variant="secondary"
                              onClick={() => {
                                if (!settingsState.subjects.includes(kw)) {
                                  settingsState.setSubjects([...settingsState.subjects, kw]);
                                }
                              }}
                            >
                              + {kw}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={() => setStep(2)} disabled={isLoading}>Back</Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        // Persist with updated subjects from SettingsState if present
                        const subjectsToSave = settingsState.subjects && settingsState.subjects.length > 0 ? settingsState.subjects : subjectMatters;
                        await updateSettingsInDatabase(
                          postingFrequency,
                          writingStyle,
                          subjectsToSave,
                          formattemplate,
                          imagePrompt,
                          imageModel,
                          negativePrompt,
                          weeklyPlanningDay,
                          contentPolicies
                        );
                        toast.success('Keywords saved');
                      } catch (e) {
                        console.error('Saving keywords failed', e);
                      }
                    }}
                    disabled={isLoading}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        const subjectsToSave = settingsState.subjects && settingsState.subjects.length > 0 ? settingsState.subjects : subjectMatters;
                        await updateSettingsInDatabase(
                          postingFrequency,
                          writingStyle,
                          subjectsToSave,
                          formattemplate,
                          imagePrompt,
                          imageModel,
                          negativePrompt,
                          weeklyPlanningDay,
                          contentPolicies
                        );
                      } catch (e) {
                        console.error('Saving keywords failed', e);
                      }
                      if (wpSettings?.is_connected) {
                        setStep(4);
                      } else {
                        onComplete();
                      }
                    }}
                    disabled={isLoading}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>WordPress categories</CardTitle>
                  <CardDescription>Fetch your current categories and add our recommendations.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!wpSettings?.is_connected ? (
                    <div className="p-3 border rounded text-sm text-muted-foreground">
                      Connect WordPress to manage categories.
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={fetchCategoriesFromWordPress}>Fetch current categories</Button>
                        <Button variant="outline" onClick={generateCategoryIdeas}>Generate suggestions</Button>
                      </div>
                      <div className="space-y-3">
                        <Label>Existing categories</Label>
                        <div className="flex flex-wrap gap-2">
                          {settingsState.categories.map((c) => (
                            <ContentTag key={c.wp_category_id} type="category" label={c.name} />
                          ))}
                        </div>
                      </div>
                      {settingsState.generatedCategories.length > 0 && (
                        <div className="space-y-3">
                          <Label>Recommended categories</Label>
                          <div className="flex flex-wrap gap-2">
                            {settingsState.generatedCategories.map((c) => (
                              <Button key={c.slug} variant="secondary" onClick={() => addCategory(c)}>
                                + {c.name}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={() => setStep(3)} disabled={isLoading}>Back</Button>
                <div className="flex gap-2">
                  <Button onClick={onComplete} disabled={isLoading}>Finish</Button>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

          {/* WordPress Instructions Modal */}
          <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Connect WordPress</DialogTitle>
            <DialogDescription>
              Choose your preferred method to connect your WordPress site
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0">
            {/* Toggle between Simple and Advanced */}
            <div className="flex items-center justify-center p-4 bg-muted rounded-lg">
              <div className="flex items-center space-x-3">
              <span className={`text-sm font-medium transition-colors ${usePlugin ? 'text-primary' : 'text-muted-foreground'}`}>
                Simple (Plugin)
              </span>
              <button
                onClick={() => setUsePlugin(!usePlugin)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                  usePlugin ? 'bg-primary' : 'bg-muted-foreground/20'
                }`}
                aria-label={`Switch to ${usePlugin ? 'Advanced' : 'Simple'} mode`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    usePlugin ? 'translate-x-1' : 'translate-x-6'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium transition-colors ${!usePlugin ? 'text-primary' : 'text-muted-foreground'}`}>
                Advanced (Manual)
              </span>
            </div>
          </div>

          {/* Simple Plugin Approach */}
          {usePlugin && (
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                    1
                  </div>
                  <div>
                    <h3 className="font-medium">Download Plugin</h3>
                    <p className="text-sm text-muted-foreground">
                      Download our WordPress plugin that handles everything automatically.
                    </p>
                    <div className="mt-2">
                                          <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={downloadPluginZip}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download Plugin
                      </Button>
                      
                      {pluginDownloaded && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={openWordPressPluginsPage}
                        >
                          <Globe className="mr-2 h-4 w-4" />
                          Open WordPress
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  2
                </div>
                <div>
                  <h3 className="font-medium">Install & Activate Plugin</h3>
                  <p className="text-sm text-muted-foreground">
                    In your WordPress admin, go to Plugins → Add New → Upload Plugin and install the downloaded zip file.
                  </p>
                  <div className="mt-2">
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          if (!wpUrl) return;
                          let cleanUrl = wpUrl.trim();
                          if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
                            cleanUrl = 'https://' + cleanUrl;
                          }
                          cleanUrl = cleanUrl.replace(/\/+$/, '');
                          window.open(`${cleanUrl}/wp-admin/plugins.php`, '_blank');
                        }}
                      >
                        <Globe className="mr-2 h-4 w-4" />
                        Open Plugins Page
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-sm text-amber-800">
                      <strong>Important:</strong> After installing, make sure to <strong>activate the plugin</strong> by clicking "Activate" next to "ContentGardener Connector" in your plugins list.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  3
                </div>
                <div>
                  <h3 className="font-medium">Get Connection Details</h3>
                  <p className="text-sm text-muted-foreground">
                    After activation, go to "ContentGardener" in your WordPress admin menu and copy the connection details.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                  4
                </div>
                <div>
                  <h3 className="font-medium">Enter Connection Details</h3>
                  <p className="text-sm text-muted-foreground">
                    Enter the WordPress URL, username, and application password below.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <div className="space-y-2">
                <Label htmlFor="wpUsername">WordPress Username</Label>
                <Input
                  id="wpUsername"
                  value={wpUsername}
                  onChange={(e) => setWpUsername(e.target.value)}
                  placeholder="Enter username from plugin"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="wpPassword">Application Password</Label>
                <Input
                  id="wpPassword"
                  type="text"
                  className="font-mono"
                  value={wpPassword}
                  onChange={(e) => setWpPassword(e.target.value)}
                  placeholder="Paste application password from plugin"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Copy this from the ContentGardener plugin in your WordPress admin
                </p>
              </div>

              <div className="flex items-center justify-center pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={parseWordPressData}
                  className="flex items-center gap-2"
                >
                  <Clipboard className="h-4 w-4" />
                  Insert All Data from Clipboard
                </Button>
                <p className="text-xs text-muted-foreground ml-3">
                  Paste the copied data from WordPress plugin and click this button
                </p>
              </div>

              <Button 
                onClick={handleWordPressCredentials} 
                disabled={!wpUsername || !wpPassword || isLoading}
                className="w-full"
              >
                {isLoading ? 'Connecting...' : 'Connect WordPress'}
              </Button>
            </div>

                         {error && (
               <div className="text-sm text-red-500 bg-red-50 p-3 rounded border border-red-200">
                 {error}
               </div>
             )}
           </div>
          )}

          {/* Advanced Manual Approach */}
          {!usePlugin && (
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                    1
                  </div>
                  <div>
                    <h3 className="font-medium">Open WordPress Admin</h3>
                    <p className="text-sm text-muted-foreground">
                      Click the button below to open your WordPress admin panel in a new tab.
                    </p>
                    <div className="mt-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={openWordPressAdmin}
                      >
                        <Globe className="mr-2 h-4 w-4" />
                        Open WordPress Admin
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                    2
                  </div>
                  <div>
                    <h3 className="font-medium">Create Application Password</h3>
                    <p className="text-sm text-muted-foreground">
                      In the WordPress admin, scroll down to "Application Passwords" section. 
                      Enter "ContentGardener" as the name and click "Add New Application Password".
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                    3
                  </div>
                  <div>
                    <h3 className="font-medium">Copy the Password</h3>
                    <p className="text-sm text-muted-foreground">
                      WordPress will generate a password like "xxxx xxxx xxxx xxxx". 
                      Copy this password exactly as shown.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                    4
                  </div>
                  <div>
                    <h3 className="font-medium">Enter Credentials Below</h3>
                    <p className="text-sm text-muted-foreground">
                      Enter your WordPress username and the application password you just created.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 border-t pt-4">
                <div className="space-y-2">
                  <Label htmlFor="wpUsername">WordPress Username</Label>
                  <Input
                    id="wpUsername"
                    type="text"
                    placeholder="Enter your WordPress username"
                    value={wpUsername}
                    onChange={(e) => setWpUsername(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="wpPassword">Application Password</Label>
                  <Input
                    id="wpPassword"
                    type="password"
                    placeholder="xxxx xxxx xxxx xxxx"
                    value={wpPassword}
                    onChange={(e) => setWpPassword(e.target.value)}
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    Paste the application password exactly as shown in WordPress
                  </p>
                </div>

                <div className="flex items-center justify-center pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={parseWordPressData}
                    className="flex items-center gap-2"
                  >
                    <Clipboard className="h-4 w-4" />
                    Insert All Data from Clipboard
                  </Button>
                  <p className="text-xs text-muted-foreground ml-3">
                    Paste the copied data from WordPress plugin and click this button
                  </p>
                </div>

                <Button 
                  onClick={handleWordPressCredentials} 
                  disabled={isLoading || !wpUsername || !wpPassword}
                  className="w-full"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    'Connect WordPress'
                  )}
                </Button>
              </div>

              {error && (
                <div className="text-sm text-red-500 bg-red-50 p-3 rounded border border-red-200">
                  {error}
                </div>
              )}
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
